import { API_BASE_URL } from '../common/ApiClient';
import { listToTree, TreeNode } from '../common/form';
import {
  getMeta,
  META_KEY_NAME,
  ROW_UNIQUE_KEY_NAME,
  UUIDType,
} from '../common/utils';

export const ClassifierExcelUrl =
  API_BASE_URL + '/classifiers{/id}/excel{?sections}';

export const getSubCategoryTypeOneLetterCode = (subCategoryType?: string) =>
  subCategoryType === 'category'
    ? ('K' as const)
    : subCategoryType == 'category_group' ||
      subCategoryType === 'combined_group'
    ? ('G' as const)
    : ('' as const);

export interface CategoryHierarchyNodeData {
  childSequenceNumber: any;
  displayName: string;
  parentKey: UUIDType | null;
  code: string;
}

export type CategoryHierarchyNode = TreeNode<CategoryHierarchyNodeData>;

export const categoriesToHierarchy = <
  T extends {
    [ROW_UNIQUE_KEY_NAME]: UUIDType;
    [META_KEY_NAME]?: { addedInUi: boolean };
    parentKey: UUIDType | null;
    code: string;
    name: string;
    childSequenceNumber: number;
  }
>(
  categories: T[]
): CategoryHierarchyNode[] => {
  // If user has added any new categories, then sort them alphabetically after the other existing ones
  const sortedCategories = categories.toSorted((a, b) => {
    if (getMeta(a)?.addedInUi && getMeta(b)?.addedInUi) {
      return a.code.localeCompare(b.code);
    }
    return (
      (a.childSequenceNumber || Infinity) - (b.childSequenceNumber || Infinity)
    );
  });

  const countsByParent: Map<UUIDType | null, number> = new Map();
  sortedCategories.forEach((category) => {
    const value = (countsByParent.get(category.parentKey) || 0) + 1;
    countsByParent.set(category.parentKey, value);
    category.childSequenceNumber = value;
  });

  let orphanCount = 0;

  const tree = listToTree(
    sortedCategories,
    ({ code, name, childSequenceNumber, parentKey }, isRootLevel) => {
      if (isRootLevel && parentKey) {
        orphanCount++;
      }
      return {
        childSequenceNumber,
        displayName: `${code} - ${name}`,
        parentKey,
        code,
      };
    }
  );

  if (!orphanCount) {
    return tree;
  }

  const orphans: CategoryHierarchyNode[] = [];
  const roots: CategoryHierarchyNode[] = [];

  tree.forEach((node) => {
    if (node.parentKey) {
      orphanCount--;
      orphans.push({ ...node, childSequenceNumber: tree.length - orphanCount });
    } else {
      roots.push({ ...node, childSequenceNumber: roots.length + 1 });
    }
  });

  return roots.concat(orphans);
};

export const mapParentCodeToParentKey = <
  T extends {
    code: string;
    parentCode: string | null;
    [ROW_UNIQUE_KEY_NAME]: UUIDType;
    name: string;
    childSequenceNumber: number;
  }
>(
  ...categoryPartitions: T[][]
) => {
  const codeToKeyMap: { [code: string]: UUIDType } = {};

  const mapCodeToKey = <
    T extends {
      parentCode: string | null;
    }
  >({
    parentCode,
    ...category
  }: T) => ({
    ...category,
    parentKey: parentCode ? codeToKeyMap[parentCode] : null,
  });

  for (const {
    code,
    [ROW_UNIQUE_KEY_NAME]: key,
  } of categoryPartitions.flat()) {
    codeToKeyMap[code] = key;
  }

  return categoryPartitions.map((partition) => partition.map(mapCodeToKey));
};
